import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useCompany } from "../../../contexts/CompanyContext";
import { useAuth } from "../../../contexts/AuthContext";
import QRCodeDecor from "../../../components/base/QRCodeDecor";
import TeamMemberModal from "./TeamMemberModal";

export interface TeamMember {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  jobRole: string;
  reputation: number;
  joinedAt: string;
  isMe: boolean;
  employeeNumber: string;
}

const JOB_ROLE_LABELS: Record<string, string> = {
  // MusicLabel
  CEO: "대표이사", AR: "A&R 담당", Finance: "재무 담당",
  Marketing: "마케팅 담당", Legal: "법무 담당",
  // LiveEntertainment
  PD: "공연기획 PD", Ticketing: "티켓영업 MD",
  // CulturalFoundation
  Projects: "사업팀장", Fundraising: "후원개발팀", Evaluation: "사업평가위원",
  // WebtoonStudio
  Editor: "에디터", Contract: "계약 MD",
  // ContentAgency
  Creative: "크리에이티브 디렉터", AE: "영업 AE",
  Production: "영상 PD", Data: "데이터 분석가",
};

function getRankInfo(rep: number) {
  if (rep >= 81) return { label: "팀장", color: "#EAB308", bar: "#EAB308" };
  if (rep >= 61) return { label: "과장", color: "#E8375F", bar: "#E8375F" };
  if (rep >= 41) return { label: "대리", color: "#FF9F43", bar: "#FF9F43" };
  if (rep >= 21) return { label: "사원", color: "#7A9BFF", bar: "#4B6CF7" };
  return { label: "인턴", color: "#8B91A8", bar: "#5A5E72" };
}

function generateEmpNum(userId: string, createdAt: string) {
  const year = new Date(createdAt).getFullYear();
  const hash = userId.replace(/-/g, "").slice(0, 4).toUpperCase();
  return `KAP-${year}-${hash}`;
}

export default function TeamDirectory() {
  const { workspaceId } = useCompany();
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TeamMember | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);

    const { data: wm } = await supabase
      .from("workspace_members")
      .select("user_id, job_role, joined_at")
      .eq("workspace_id", workspaceId);

    if (!wm || wm.length === 0) { setLoading(false); return; }

    const ids = wm.map(m => m.user_id);

    const [{ data: profiles }, { data: states }] = await Promise.all([
      supabase.from("profiles").select("id, display_name, avatar_url, created_at").in("id", ids),
      supabase.from("company_states").select("user_id, reputation").eq("workspace_id", workspaceId).in("user_id", ids),
    ]);

    const profMap: Record<string, { display_name: string; avatar_url: string | null; created_at: string }> = {};
    (profiles ?? []).forEach(p => { profMap[p.id] = p; });

    const repMap: Record<string, number> = {};
    (states ?? []).forEach(s => { repMap[s.user_id] = s.reputation; });

    const rows: TeamMember[] = wm.map(m => {
      const p = profMap[m.user_id];
      return {
        userId: m.user_id,
        displayName: p?.display_name ?? "익명",
        avatarUrl: p?.avatar_url ?? null,
        jobRole: m.job_role ?? "-",
        reputation: repMap[m.user_id] ?? 30,
        joinedAt: m.joined_at ?? p?.created_at ?? new Date().toISOString(),
        isMe: m.user_id === user?.id,
        employeeNumber: generateEmpNum(m.user_id, p?.created_at ?? m.joined_at ?? new Date().toISOString()),
      };
    });

    rows.sort((a, b) => (b.isMe ? 1 : 0) - (a.isMe ? 1 : 0) || b.reputation - a.reputation);
    setMembers(rows);
    setLoading(false);
  }, [workspaceId, user?.id]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  if (loading) return (
    <div className="bg-[#111112] border border-white/[0.06] rounded-2xl px-5 py-4">
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-team-line text-[#555555] text-base"></i>
        <span className="text-[#555555] text-xs font-semibold uppercase tracking-widest">팀원 명부</span>
      </div>
      <div className="flex gap-3 overflow-hidden">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="w-36 h-52 rounded-xl bg-white/[0.03] animate-pulse shrink-0" />
        ))}
      </div>
    </div>
  );

  if (members.length === 0) return null;

  return (
    <>
      <div className="bg-[#111112] border border-white/[0.06] rounded-2xl px-5 py-4">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-team-line text-[#444444] text-base"></i>
            <span className="text-[#888888] text-xs font-semibold uppercase tracking-widest">팀원 명부</span>
            <span className="ml-1 text-[10px] text-[#3E4155] bg-white/[0.04] border border-white/[0.06] px-2 py-0.5 rounded-full">
              {members.length}명
            </span>
          </div>
          <button type="button" onClick={fetchMembers}
            className="w-6 h-6 flex items-center justify-center text-[#3E4155] hover:text-[#888888] transition-colors cursor-pointer">
            <i className="ri-refresh-line text-xs"></i>
          </button>
        </div>

        {/* 카드 스크롤 영역 */}
        <div className="flex gap-3 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          {members.map(member => {
            const rank = getRankInfo(member.reputation);
            const jobLabel = JOB_ROLE_LABELS[member.jobRole] ?? member.jobRole;
            const joinDate = new Date(member.joinedAt).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });

            return (
              <div
                key={member.userId}
                className="shrink-0 w-36 rounded-xl overflow-hidden relative cursor-pointer group transition-all duration-200"
                style={{
                  background: member.isMe
                    ? "linear-gradient(160deg, #1a1a2e 0%, #1C2540 60%, #0f3460 100%)"
                    : "linear-gradient(160deg, #111118 0%, #151520 100%)",
                  border: member.isMe
                    ? "1px solid rgba(75,108,247,0.35)"
                    : "1px solid rgba(255,255,255,0.05)",
                  boxShadow: member.isMe ? "0 0 20px rgba(75,108,247,0.1)" : "none",
                }}
                onClick={() => setSelected(member)}
              >
                {/* 상단 컬러 라인 */}
                <div className="h-0.5 w-full"
                  style={{ background: `linear-gradient(90deg, transparent, ${rank.color}60, transparent)` }} />

                {/* 내 카드 배지 */}
                {member.isMe && (
                  <div className="absolute top-2 right-2 text-[8px] font-bold px-1.5 py-0.5 rounded-full z-10"
                    style={{ backgroundColor: "rgba(75,108,247,0.2)", color: "#7A9BFF", border: "1px solid rgba(75,108,247,0.3)" }}>
                    나
                  </div>
                )}

                {/* 증명사진 */}
                <div className="flex justify-center pt-4 pb-2">
                  <div className="w-14 h-16 rounded-lg overflow-hidden border border-white/10"
                    style={{ background: "linear-gradient(135deg, #252830, #1C1F2E)" }}>
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} alt={member.displayName} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-user-3-line text-white/15 text-xl"></i>
                      </div>
                    )}
                  </div>
                </div>

                {/* 정보 */}
                <div className="px-3 pb-2 text-center">
                  <div className="text-white text-[11px] font-semibold leading-tight mb-1 truncate">
                    {member.displayName}
                  </div>
                  <div className="flex justify-center mb-1.5">
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ color: rank.color, backgroundColor: `${rank.color}18`, border: `1px solid ${rank.color}35` }}>
                      {rank.label}
                    </span>
                  </div>
                  <div className="text-white/30 text-[9px] leading-tight mb-2 truncate">{jobLabel}</div>
                  <div className="w-full h-0.5 bg-white/[0.06] rounded-full overflow-hidden mb-1">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${member.reputation}%`, backgroundColor: rank.bar, opacity: 0.5 }} />
                  </div>
                  <div className="text-white/20 text-[8px] mb-2">{member.reputation}pt</div>
                </div>

                {/* QR 코드 + 입사일 */}
                <div className="px-3 pb-3 flex flex-col items-center gap-1">
                  <QRCodeDecor
                    seed={member.userId}
                    cellSize={2}
                    opacity={0.2}
                    link={`${window.location.origin}/profile?uid=${member.userId}`}
                    tooltip={`${member.displayName}의 프로필 링크`}
                  />
                  <div className="text-white/10 text-[7px] font-mono truncate w-full text-center">
                    {member.employeeNumber}
                  </div>
                  <div className="text-white/15 text-[7px]">{joinDate} 입사</div>
                </div>

                {/* 호버 오버레이 */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/[0.04] flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                  <span className="text-white/60 text-[9px] flex items-center gap-1 bg-black/40 px-2 py-1 rounded-full">
                    <i className="ri-eye-line text-[10px]"></i>
                    {member.isMe ? "프로필 보기" : "상세 보기"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 팀원 상세 모달 */}
      {selected && (
        <TeamMemberModal
          member={selected}
          workspaceId={workspaceId}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
